import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FiArrowLeft, FiDownload } from 'react-icons/fi';
import { apiJson, extractApiErrorMessage } from '@/lib/api';
import { downloadProfessionalSectionExport } from '@/lib/pro-client-export';
import type {
  HouseholdResponse,
  ProfessionalClientListResponse,
  ProfessionalInviteResponse,
} from '@/lib/types';

function coparentDisplayName(inv: ProfessionalInviteResponse): string {
  if (inv.coparent_name?.trim()) return inv.coparent_name.trim();
  return 'Co-parent';
}

function ProClientLayout() {
  const { inviteId } = Route.useParams();
  const id = Number(inviteId);
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [reportDownloading, setReportDownloading] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () => apiJson<ProfessionalClientListResponse>(getToken, '/v1/professional/clients'),
    enabled: isLoaded && !!isSignedIn,
  });

  const invite = useMemo(
    () => clientsQuery.data?.clients.find((c) => c.id === id),
    [clientsQuery.data, id]
  );

  const householdQuery = useQuery({
    queryKey: ['professional-client-household', id],
    queryFn: () =>
      apiJson<HouseholdResponse>(
        getToken,
        `/v1/professional/clients/${id}/household`
      ),
    enabled: isLoaded && !!isSignedIn && !!invite,
  });
  const soloHousehold = (householdQuery.data?.members?.length ?? 0) <= 1;

  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  const base = `/pro/clients/${inviteId}`;

  const tabClass = (suffix: string) => {
    const full = `${base}${suffix}`;
    const active = pathname === full || pathname === `${full}/`;
    return active
      ? 'border-[#018A9D] text-[#018A9D] font-semibold'
      : 'border-transparent text-gray-600 hover:text-gray-900';
  };

  if (clientsQuery.isLoading || !isLoaded) {
    return (
      <div className='max-w-4xl mx-auto px-6 py-12 flex justify-center' style={bodyStyle}>
        <div className='animate-spin h-8 w-8 border-2 border-[#018A9D] border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className='max-w-4xl mx-auto px-6 py-12' style={bodyStyle}>
        <p className='text-gray-600 mb-4'>Client not found or you no longer have access.</p>
        <Link to='/pro' className='text-[#018A9D] font-semibold'>
          Back to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light' style={bodyStyle}>
      <div className='max-w-4xl mx-auto px-6 py-8'>
        <Link
          to='/pro'
          className='inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 mb-6'
        >
          <FiArrowLeft className='w-4 h-4' />
          Dashboard
        </Link>

        <h1 className='text-2xl font-semibold text-gray-900 mb-1'>{coparentDisplayName(invite)}</h1>
        <p className='text-gray-500 text-sm mb-6'>Read-only shared records</p>

        {(invite.share_custody_calendar || invite.share_expenses) ? (
          <div className='mb-6 rounded-2xl border border-[#018A9D]/25 bg-white p-4 shadow-sm'>
            <p className='text-sm font-semibold text-gray-900 mb-1'>Lawyer reports</p>
            <p className='text-xs text-gray-500 mb-3'>
              Court-ready PDFs generated from shared Harbor records.
            </p>
            {reportError ? (
              <p className='mb-3 text-xs text-red-700'>{reportError}</p>
            ) : null}
            <div className='flex flex-wrap gap-2'>
              {invite.share_custody_calendar ? (
                <button
                  type='button'
                  disabled={reportDownloading !== null}
                  onClick={async () => {
                    setReportError(null);
                    setReportDownloading('exchange');
                    try {
                      await downloadProfessionalSectionExport(getToken, id, 'exchange', {
                        soloHousehold,
                      });
                    } catch (e: unknown) {
                      setReportError(
                        extractApiErrorMessage(e, 'Could not download Compliance Report.')
                      );
                    } finally {
                      setReportDownloading(null);
                    }
                  }}
                  className='inline-flex items-center gap-1.5 rounded-xl bg-[#018A9D] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#017d8d] disabled:opacity-50'
                >
                  <FiDownload className='h-3.5 w-3.5' />
                  {reportDownloading === 'exchange'
                    ? 'Preparing…'
                    : 'Compliance Report'}
                </button>
              ) : null}
              {invite.share_expenses ? (
                <button
                  type='button'
                  disabled={reportDownloading !== null}
                  onClick={async () => {
                    setReportError(null);
                    setReportDownloading('expense_disputes');
                    try {
                      await downloadProfessionalSectionExport(
                        getToken,
                        id,
                        'expense_disputes',
                        { soloHousehold }
                      );
                    } catch (e: unknown) {
                      setReportError(
                        extractApiErrorMessage(
                          e,
                          'Could not download Expense Dispute Summary.'
                        )
                      );
                    } finally {
                      setReportDownloading(null);
                    }
                  }}
                  className='inline-flex items-center gap-1.5 rounded-xl border border-[#018A9D] px-3.5 py-2 text-xs font-semibold text-[#018A9D] hover:bg-[#018A9D]/5 disabled:opacity-50'
                >
                  <FiDownload className='h-3.5 w-3.5' />
                  {reportDownloading === 'expense_disputes'
                    ? 'Preparing…'
                    : 'Expense Dispute Summary'}
                </button>
              ) : null}
              {invite.share_custody_calendar || invite.share_expenses ? (
                <button
                  type='button'
                  disabled={reportDownloading !== null}
                  onClick={async () => {
                    setReportError(null);
                    setReportDownloading('hearing_prep');
                    try {
                      await downloadProfessionalSectionExport(
                        getToken,
                        id,
                        'hearing_prep',
                        { soloHousehold }
                      );
                    } catch (e: unknown) {
                      setReportError(
                        extractApiErrorMessage(e, 'Could not download Hearing Prep Brief.')
                      );
                    } finally {
                      setReportDownloading(null);
                    }
                  }}
                  className='inline-flex items-center gap-1.5 rounded-xl border border-gray-300 px-3.5 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50'
                >
                  <FiDownload className='h-3.5 w-3.5' />
                  {reportDownloading === 'hearing_prep'
                    ? 'Preparing…'
                    : 'Hearing Prep Brief'}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <nav className='flex flex-wrap gap-2 border-b border-gray-200 mb-6'>
          {invite.share_chats ? (
            <Link
              to='/pro/clients/$inviteId/chat'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/chat')}`}
            >
              Chat
            </Link>
          ) : null}
          {invite.share_expenses ? (
            <Link
              to='/pro/clients/$inviteId/expenses'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/expenses')}`}
            >
              Expenses
            </Link>
          ) : null}
          {invite.share_custody_calendar ? (
            <Link
              to='/pro/clients/$inviteId/calendar'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/calendar')}`}
            >
              Calendar
            </Link>
          ) : null}
          {invite.share_journal ? (
            <Link
              to='/pro/clients/$inviteId/journal'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/journal')}`}
            >
              Journal
            </Link>
          ) : null}
        </nav>

        <Outlet />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/pro/clients/$inviteId')({
  component: ProClientLayout,
});
